import { useCallback, useLayoutEffect, useState } from 'react';

export type AppPage = 'home' | 'favorites' | 'library';
type LocationState = { pathname: string; hash: string; key: number; scroll?: number };
const currentLocation = (key = 0, scroll?: number): LocationState => ({
  pathname: window.location.pathname.replace(/\/$/, '') || '/',
  hash: window.location.hash,
  key,
  scroll,
});

export function useAppNavigation() {
  const [location, setLocation] = useState(currentLocation);
  useLayoutEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    const onPopState = (event: PopStateEvent) => {
      setLocation((current) => currentLocation(current.key + 1, event.state?.mujianScroll ?? 0));
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      history.scrollRestoration = previous;
    };
  }, []);

  const navigate = useCallback((href: string) => {
    const destination = new URL(href, window.location.origin);
    if (destination.origin !== window.location.origin) return;
    const next = destination.pathname + destination.search + destination.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    // Save the current reading position before creating a distinct browser-history entry.
    history.replaceState({ ...history.state, mujianScroll: window.scrollY }, '', current);
    if (next !== current) history.pushState({ mujianScroll: 0 }, '', next);
    setLocation((location) => currentLocation(location.key + 1));
  }, []);

  useLayoutEffect(() => {
    if (location.scroll !== undefined) {
      window.scrollTo({ top: location.scroll, behavior: 'instant' });
    } else if (location.hash) {
      let id = location.hash.slice(1);
      try {
        id = decodeURIComponent(id);
      } catch {
        /* Keep malformed fragments inert. */
      }
      const target = document.getElementById(id);
      target?.scrollIntoView({ block: 'start', behavior: 'instant' });
    } else if (location.key > 0 || location.pathname !== '/') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    const headings: Record<string, string> = {
      '/favorites': '我的收藏 · 幕间 MUJIAN',
      '/library': '我拥有的 · 幕间 MUJIAN',
      '/owned': '我拥有的 · 幕间 MUJIAN',
    };
    document.title = headings[location.pathname] ?? '幕间 MUJIAN — 把片刻心动，留在日常';
    if (location.key > 0) {
      const heading = document.querySelector<HTMLElement>('main h1');
      heading?.setAttribute('tabindex', '-1');
      heading?.focus({ preventScroll: true });
    }
  }, [location]);

  const page: AppPage =
    location.pathname === '/favorites'
      ? 'favorites'
      : location.pathname === '/library' || location.pathname === '/owned'
        ? 'library'
        : 'home';
  return { page, hash: location.hash, locationKey: location.key, navigate };
}
